import ApiClient from "../helpers/ApiClient";
import useSWR from "swr";
import axios from "axios";
import OfflineStorage from "../helpers/OfflineStorage";
import OfflineQueue from "../helpers/OfflineQueue";
import { isElectronApp } from "../config/config";
import { getUserDetailsInLocalStorage } from "../helpers/UserDetails";

const fetcher = async (url) => {
  const isOnline = OfflineStorage.isClientOnline();
  if (!isOnline) {
    const cached = OfflineStorage.getCachedInvoices();
    return Array.isArray(cached) ? cached : [];
  }
  try {
    const res = await ApiClient.get(url);
    let combined = Array.isArray(res.data) ? [...res.data] : [];
    
    // Merge only genuinely pending offline invoices from OfflineQueue (avoids re-injecting stale OFF-INV-* cache entries)
    const pendingQueueIds = new Set(
      (OfflineQueue.getOfflineOrders() || [])
        .filter(q => q.status !== 'synced' && (q.type === 'PAY_AND_COMPLETE' || q.type === 'ORDER_AND_INVOICE'))
        .map(q => String(q.id))
    );
    const cached = OfflineStorage.getCachedInvoices() || [];
    const cloudInvIds = new Set(combined.map(i => String(i.invoice_id || i.id || '')));
    const offlinePending = (Array.isArray(cached) ? cached : []).filter(inv => {
      const invIdStr = String(inv.invoice_id || inv.id || '');
      const recIdStr = String(inv.id || '');
      const isOfflineInv = invIdStr.startsWith('OFF-INV-') || invIdStr.includes('-OFF-');
      return isOfflineInv && pendingQueueIds.has(recIdStr) && !cloudInvIds.has(invIdStr);
    });

    if (offlinePending.length > 0) {
      combined = [...offlinePending, ...combined];
    }

    OfflineStorage.cacheInvoices(combined);
    return combined;
  } catch (err) {
    const cached = OfflineStorage.getCachedInvoices();
    if (Array.isArray(cached) && cached.length > 0) {
      return cached;
    }
    throw err;
  }
};

export function useInvoices({ type, from = null, to = null } = {}) {
  const APIURL = `/invoices?type=${type}&from=${from}&to=${to}`;
  const { data, error, isLoading, mutate } = useSWR(APIURL, fetcher, { 
    keepPreviousData: true,
    fallbackData: OfflineStorage.getCachedInvoices() || []
  });
  return {
    data,
    error,
    isLoading,
    mutate,
    APIURL,
  };
}

export async function getInvoicesInit() {
  const isOnline = OfflineStorage.isClientOnline();
  if (!isOnline) {
    const cached = OfflineStorage.getCachedInvoicesInit();
    if (cached) return { status: 200, data: cached, _isOffline: true };
    const bootstrap = OfflineStorage.getCachedBootstrap();
    if (bootstrap) {
      return {
        status: 200,
        data: {
          printSettings: bootstrap.printSettings || {},
          storeSettings: bootstrap.storeSettings || {},
          paymentTypes: bootstrap.paymentTypes || []
        },
        _isOffline: true
      };
    }
  }

  try {
    const res = await ApiClient.get("/invoices/init");
    if (res.status === 200 && res.data) {
      OfflineStorage.cacheInvoicesInit(res.data);
    }
    return res;
  } catch (error) {
    const cached = OfflineStorage.getCachedInvoicesInit() || OfflineStorage.getCachedBootstrap();
    if (cached) {
      return {
        status: 200,
        data: {
          printSettings: cached.printSettings || {},
          storeSettings: cached.storeSettings || {},
          paymentTypes: cached.paymentTypes || []
        },
        _isOffline: true
      };
    }
    throw error;
  }
}

export async function getInvoiceOrders(invoiceId, orderIds) {
  try {
    const res = await ApiClient.post("/invoices/orders", {
      invoiceId,
      orderIds
    });
    if (res && res.status >= 200 && res.status < 300) {
      return res;
    }
  } catch (error) {
    console.warn("[getInvoiceOrders] Primary ApiClient call error:", error?.message);
  }

  // If running in Electron, try querying the local bundled backend directly on port 5001
  if (isElectronApp()) {
    try {
      const user = getUserDetailsInLocalStorage() || {};
      const token = user.token || '';
      const localHost = (typeof window !== "undefined" && window.location.hostname) ? window.location.hostname : "127.0.0.1";
      const localPort = (typeof window !== "undefined" && window.location.port) ? window.location.port : "5001";
      const localUrl = `http://${localHost}:${localPort}/api/v1/invoices/orders`;

      const localRes = await axios.post(localUrl, {
        invoiceId,
        orderIds
      }, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        withCredentials: true,
        timeout: 4000
      });
      if (localRes && localRes.status >= 200 && localRes.status < 300) {
        return localRes;
      }
    } catch (localErr) {
      console.warn("[getInvoiceOrders] Direct local backend fallback note:", localErr.message);
    }
  }

  // If offline/local, check if it's an offline generated order from OfflineQueue
  try {
    const offlineOrders = OfflineQueue.getOfflineOrders() || [];
    const matched = offlineOrders.find(o => String(o?.id) === String(invoiceId) || (o?.payload && String(o.payload?.orderId) === String(invoiceId)));
    if (matched && matched.payload && matched.payload.cartItems) {
      return {
        status: 200,
        data: [{
          id: matched.id,
          token_no: matched.id,
          items: matched.payload.cartItems
        }],
        _isOffline: true
      };
    }
  } catch (qErr) {
    console.warn("[getInvoiceOrders] Offline queue check error:", qErr);
  }

  // Return a safe non-crashing payload so calling UI components never crash
  return {
    status: 200,
    data: {
      orders: [],
      invoiceId: invoiceId,
      invoice_no: `INV-${invoiceId}`
    },
    _isFallback: true
  };
}

export async function searchInvoices(query) {
  try {
    const response = await ApiClient.get(`/invoices/search?q=${encodeURIComponent(query)}`);
    return response;
  } catch (error) {
    throw error;
  }
}
export async function collectDue({ invoice_id, amount, payment_date, payment_method, notes, multi_payments }) {
  try {
    const res = await ApiClient.post("/invoices/collect-due", {
      invoice_id,
      amount,
      payment_date,
      payment_method,
      notes,
      multi_payments
    });
    return res;
  } catch (error) {
    throw error;
  }
}

const customerDuesFetcher = async (url) => {
  try {
    const res = await ApiClient.get(url);
    if (res?.data && Array.isArray(res.data)) {
      try {
        localStorage.setItem("cached_customer_dues", JSON.stringify(res.data));
      } catch (e) {}
    }
    return res.data;
  } catch (err) {
    try {
      const cached = localStorage.getItem("cached_customer_dues");
      if (cached) return JSON.parse(cached);
    } catch (e) {}
    throw err;
  }
};

const statementFetcher = async (url) => {
  try {
    const res = await ApiClient.get(url);
    if (res?.data) {
      try {
        localStorage.setItem(`cached_statement_${url}`, JSON.stringify(res.data));
      } catch (e) {}
    }
    return res.data;
  } catch (err) {
    try {
      const cached = localStorage.getItem(`cached_statement_${url}`);
      if (cached) return JSON.parse(cached);
    } catch (e) {}
    throw err;
  }
};

export function useCustomerDues(search = "", from = null, to = null) {
  const APIURL = `/invoices/customer-dues?search=${encodeURIComponent(search)}&from=${from}&to=${to}`;
  const { data, error, isLoading, mutate } = useSWR(APIURL, customerDuesFetcher, { keepPreviousData: true });
  return {
    data,
    error,
    isLoading,
    mutate,
    APIURL,
  };
}

export function useCustomerStatement(customerId, from = "", to = "") {
  const query = [];
  if (from) query.push(`from=${from}`);
  if (to) query.push(`to=${to}`);
  const qStr = query.length > 0 ? `?${query.join("&")}` : "";
  const APIURL = customerId ? `/invoices/customer-statement/${encodeURIComponent(customerId)}${qStr}` : null;
  const { data, error, isLoading, mutate } = useSWR(APIURL, statementFetcher);
  return {
    data,
    error,
    isLoading,
    mutate,
    APIURL,
  };
}

export async function collectCustomerDue({ customer_id, amount, payment_method, notes, multi_payments, payment_date }) {
  try {
    const res = await ApiClient.post("/invoices/collect-customer-due", {
      customer_id,
      amount,
      payment_method,
      notes,
      multi_payments,
      payment_date
    });
    return res;
  } catch (error) {
    throw error;
  }
}

export async function voidInvoice(invoiceId, reason, options = {}) {
  try {
    const payload = typeof invoiceId === 'object' ? invoiceId : { invoiceId, reason, ...options };
    const res = await ApiClient.post("/invoices/sales-return", payload);
    return res;
  } catch (error) {
    throw error;
  }
}

export async function processSalesReturn({ invoiceId, reason, paymentMethod, returnDate }) {
  try {
    const res = await ApiClient.post("/invoices/sales-return", { invoiceId, reason, paymentMethod, returnDate });
    return res;
  } catch (error) {
    throw error;
  }
}

export async function addManualCustomerDue({ customer_id, amount, date, notes }) {
  try {
    const res = await ApiClient.post("/invoices/manual-due", { customer_id, amount, date, notes });
    return res;
  } catch (error) {
    throw error;
  }
}

export async function reassignInvoiceDate(invoiceIds, targetDate, targetTime = null) {
  try {
    const ids = Array.isArray(invoiceIds) ? invoiceIds : [invoiceIds];
    const res = await ApiClient.post("/invoices/reassign-date", {
      invoice_ids: ids,
      target_date: targetDate,
      target_time: targetTime
    });
    return res;
  } catch (error) {
    throw error;
  }
}
